import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ExternalLink, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

// Per-region Google Sheets configuration. Must mirror the edge function map.
const REGION_SHEETS: Record<string, string> = {
  Southern: "15qeIxIlQ-j_NvCEpl61Aq8j_qMx8eiKX4BKWiLDlCmc",
  Southeast: "1NdQSUrUCUAQ6GPyf8-zLcLqqjpntH-6yrp52_B0Yutc",
  Eastern: "1C845eCCGPk02JGbrQGWz-cJ0SHu9D6NUnA2gqZCI6VI",
  Midwest: "1K9NBu-3tN4wtd6W1YQgGCBPhoQe9qpNkwKcVMlpncko",
  Northwest: "1wha3Ob7O0U80I6mE62qdg8Bt33SwAUU0g1YbJSIJWVA",
  Southwest: "1gitZjmfpdtixlrDHuSbHi8QUKrGl_VrB6PRA9C5uc3E",
};

interface RegionSyncState {
  last_sync_at?: string;
  status?: "ok" | "error";
  error?: string;
  registrations?: number;
  members?: number;
  sheet_id?: string;
  sheet_url?: string;
}

interface SyncState {
  regions?: Record<string, RegionSyncState>;
}

interface Props {
  userRole?: string;
  userRegionId?: string | null;
}

export const LiveSheetSyncPanel = ({ userRole, userRegionId }: Props) => {
  const [state, setState] = useState<SyncState>({});
  const [syncing, setSyncing] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRegionName, setUserRegionName] = useState<string | null>(null);

  const isGlobal = userRole === "global_admin" || userRole === "president";

  // Resolve the regional admin's region name
  useEffect(() => {
    if (isGlobal || !userRegionId) {
      setUserRegionName(null);
      return;
    }
    supabase
      .from("regions")
      .select("name")
      .eq("id", userRegionId)
      .maybeSingle()
      .then(({ data }) => setUserRegionName(data?.name ?? null));
  }, [isGlobal, userRegionId]);

  const visibleRegions = useMemo(() => {
    const all = Object.keys(REGION_SHEETS);
    if (isGlobal) return all;
    if (userRegionName && REGION_SHEETS[userRegionName]) return [userRegionName];
    return [];
  }, [isGlobal, userRegionName]);

  const loadStatus = async () => {
    const { data } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "live_sheet_sync")
      .maybeSingle();
    if (data?.value) setState(data.value as SyncState);
    setLoading(false);
  };

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 30000);
    return () => clearInterval(t);
  }, []);

  // supabase.functions.invoke only reports "non-2xx status code"; read the JSON body
  // the edge function returned so admins see the real reason.
  const describeInvokeError = async (error: any): Promise<string> => {
    try {
      const res = error?.context;
      if (res && typeof res.json === "function") {
        const body = await res.clone().json().catch(() => null);
        if (body?.error) return String(body.error);
        const text = await res.clone().text().catch(() => "");
        if (text) return text.slice(0, 300);
      }
    } catch (_) { /* ignore */ }
    return error?.message || String(error);
  };

  const syncRegion = async (region: string) => {
    setSyncing(region);
    try {
      const { data, error } = await supabase.functions.invoke("live-sync-registrations", {
        body: { region },
      });
      if (error) throw new Error(await describeInvokeError(error));
      const r = data?.results?.[region];
      if (!data?.success || !r?.success) {
        throw new Error(r?.error || data?.error || "Sync failed");
      }
      toast({
        title: `${region} synced`,
        description: `${r.registrations} registrations pushed (duplicates & test entries removed)`,
      });
      await loadStatus();
    } catch (e: any) {
      toast({ title: `Sync failed (${region})`, description: e.message || String(e), variant: "destructive" });
    } finally {
      setSyncing(null);
    }
  };

  const fullResync = async () => {
    setSyncing("__all__");
    try {
      let totalRegs = 0;
      let failed: string[] = [];
      for (const region of visibleRegions) {
        try {
          const { data, error } = await supabase.functions.invoke("live-sync-registrations", {
            body: { region },
          });
          if (error) throw new Error(await describeInvokeError(error));
          const r = data?.results?.[region];
          if (!r?.success) throw new Error(r?.error || data?.error || "Sync failed");
          totalRegs += r.registrations || 0;
        } catch (e: any) {
          failed.push(`${region}: ${e.message || e}`);
        }
        // Space out regions so we don't trip the Sheets per-minute quota
        await new Promise((res) => setTimeout(res, 2000));
      }


      if (failed.length) {
        toast({
          title: `Full resync completed with errors`,
          description: `${totalRegs} rows pushed. Failed: ${failed.join("; ")}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: `Full resync complete`,
          description: `${totalRegs} registrations pushed across ${visibleRegions.length} region${visibleRegions.length === 1 ? "" : "s"}. All duplicates and test entries cleaned.`,
        });
      }
      await loadStatus();
    } finally {
      setSyncing(null);
    }
  };


  if (!isGlobal && !userRegionName) {
    // Regional admin without resolvable region — render nothing
    return null;
  }

  if (visibleRegions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Live Google Sheet Sync
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No Google Sheet has been configured for your region yet. Please contact the global admin to set one up.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
          Live Google Sheet Sync
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Each region pushes its own registrations and member rosters into a dedicated Google Sheet. Only the region's
          admin (and global admins) can see and sync that region's sheet.
        </p>

        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex-1 min-w-[220px] text-xs text-amber-900">
            <strong>Clean up duplicates:</strong> resync every visible region. Existing sheet rows are wiped and
            rewritten with the deduped, test-entry-free dataset.
          </div>
          <Button
            onClick={fullResync}
            disabled={syncing !== null}
            size="sm"
            variant="default"
          >
            {syncing === "__all__" ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Full Resync ({visibleRegions.length})
          </Button>
        </div>


        <div className="grid gap-3 md:grid-cols-2">
          {visibleRegions.map((region) => {
            const s = state.regions?.[region] ?? {};
            const sheetId = REGION_SHEETS[region];
            const sheetUrl = s.sheet_url || `https://docs.google.com/spreadsheets/d/${sheetId}/edit`;
            const isOk = s.status === "ok";
            const isErr = s.status === "error";
            const busy = syncing === region;
            return (
              <div key={region} className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{region} Region</div>
                  {isOk && <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Synced</Badge>}
                  {isErr && <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" /> Error</Badge>}
                  {!isOk && !isErr && <Badge variant="secondary">Not synced yet</Badge>}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" size="sm">
                    <a href={sheetUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" /> Open Sheet
                    </a>
                  </Button>
                  <Button onClick={() => syncRegion(region)} disabled={busy} size="sm">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Sync now
                  </Button>
                </div>

                <div className="text-xs text-muted-foreground space-y-0.5">
                  {loading ? (
                    <div>Loading status…</div>
                  ) : (
                    <>
                      {s.last_sync_at && (
                        <div>Last sync: {new Date(s.last_sync_at).toLocaleString()}</div>
                      )}
                      {typeof s.registrations === "number" && (
                        <div>Registrations pushed: {s.registrations}</div>
                      )}
                      {s.error && <div className="text-destructive">Error: {s.error}</div>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-xs text-muted-foreground">
          Tabs auto-created in each sheet: Summary, Registrations, Members. Only data for that region is pushed.
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveSheetSyncPanel;
